import type { ColumnDef } from "@tanstack/react-table";
import { ExternalLink, ImageUp, RotateCcw } from "lucide-react";
import { useRef } from "react";

import { DataTable } from "@/components/admin/DataTable";
import { StatusBadge } from "@/components/admin/StatusBadge";
import {
  useBadgeArtwork,
  useResetBadgeArtwork,
  useUploadBadgeArtwork,
} from "@/hooks/admin/certificates";
import type { BadgeStatus } from "@/types";

/**
 * Participation badges for a Dominion Free Cohort (KC-497).
 *
 * Badges are issued automatically by the Human Review that completes a
 * Participant's last Work Package, so there is nothing to send here — only who
 * has theirs, how close everyone else is, and the artwork every badge carries.
 */

function formatDate(value: string): string {
  return new Date(value).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function BadgeCell({ status }: { status: BadgeStatus }) {
  if (!status.badge_id) return <StatusBadge tone="neutral">Not yet</StatusBadge>;
  if (status.revoked) return <StatusBadge tone="danger">Revoked</StatusBadge>;
  if (status.hidden) return <StatusBadge tone="warning">Hidden by participant</StatusBadge>;
  return <StatusBadge tone="success">Badge issued</StatusBadge>;
}

const columns: ColumnDef<BadgeStatus>[] = [
  { accessorKey: "name", header: "Participant" },
  { accessorKey: "email", header: "Email", cell: ({ row }) => row.original.email ?? "—" },
  {
    id: "progress",
    header: "Human Reviews",
    cell: ({ row }) =>
      `${row.original.reviewed_work_packages} / ${row.original.total_work_packages} reviewed`,
  },
  { id: "badge", header: "Badge", cell: ({ row }) => <BadgeCell status={row.original} /> },
  {
    accessorKey: "issued_on",
    header: "Issued",
    cell: ({ row }) => (row.original.issued_on ? formatDate(row.original.issued_on) : "—"),
  },
  {
    id: "link",
    header: "",
    cell: ({ row }) =>
      row.original.badge_url && !row.original.hidden && !row.original.revoked ? (
        <a
          href={row.original.badge_url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-sm text-gray-700 hover:underline"
        >
          View <ExternalLink className="h-3 w-3" />
        </a>
      ) : null,
  },
];

function ArtworkSection({ previewUrl }: { previewUrl: string | null }) {
  const artwork = useBadgeArtwork();
  const upload = useUploadBadgeArtwork();
  const reset = useResetBadgeArtwork();
  const input = useRef<HTMLInputElement>(null);
  const custom = artwork.data?.has_custom_artwork ?? false;
  const error = upload.error ?? reset.error;
  // Cache-bust on every artwork change so the preview is the next real render.
  const version = artwork.data?.updated_at ?? "default";

  return (
    <section className="flex flex-wrap items-start gap-6 rounded-xl border border-gray-200 bg-white p-5">
      {previewUrl && (
        <img
          src={`${previewUrl}/badge.svg?v=${encodeURIComponent(version)}`}
          alt="Badge preview"
          className="h-40 w-auto rounded-lg border border-gray-100"
        />
      )}
      <div className="min-w-0 flex-1">
        <h3 className="mb-1 font-medium text-gray-900">Badge artwork</h3>
        <p className="mb-3 text-sm text-gray-500">
          {custom
            ? `Custom artwork (${artwork.data?.content_type === "image/png" ? "PNG" : "SVG"}) — every badge uses it from the next view.`
            : "Using the default badge, derived from the certificate seal."}
        </p>
        <div className="flex flex-wrap gap-2">
          <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg bg-gray-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-gray-800">
            <ImageUp className="h-4 w-4" />
            {upload.isPending ? "Uploading…" : "Upload badge artwork"}
            <input
              ref={input}
              type="file"
              accept="image/svg+xml,image/png,.svg,.png"
              className="sr-only"
              aria-label="Upload badge artwork (SVG or PNG)"
              disabled={upload.isPending}
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) upload.mutate(file);
                if (input.current) input.current.value = "";
              }}
            />
          </label>
          {custom && (
            <button
              type="button"
              disabled={reset.isPending}
              onClick={() => reset.mutate()}
              className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40"
            >
              <RotateCcw className="h-4 w-4" /> Use default
            </button>
          )}
        </div>
        {error && (
          <p className="mt-2 text-sm text-red-600">
            {error instanceof Error ? error.message : "Could not update the artwork"}
          </p>
        )}
      </div>
    </section>
  );
}

export function ParticipationBadgesPanel({ participants }: { participants: BadgeStatus[] }) {
  const issued = participants.filter((p) => p.badge_id).length;
  const previewUrl =
    participants.find((p) => p.badge_url && !p.hidden && !p.revoked)?.badge_url ?? null;

  return (
    <div className="flex h-full flex-col gap-4">
      <div>
        <h2 className="text-lg font-medium text-gray-900">Participation badges</h2>
        <p className="text-sm text-gray-500">
          Dominion Free taster. A badge is issued and emailed automatically once every Work Package
          has its Human Review. {issued} of {participants.length} participants have their badge.
        </p>
      </div>
      <ArtworkSection previewUrl={previewUrl} />
      <div className="min-h-0 flex-1">
        <DataTable
          columns={columns}
          data={participants}
          emptyMessage="Nobody is enrolled in this cohort yet."
          getRowId={(r) => r.user_programme_id}
        />
      </div>
    </div>
  );
}
